<template>
  <ValidationObserver class="d-flex flex-column h-100" v-slot="validation" ref="observer" tag="div">
    <b-form @submit.prevent="validation.handleSubmit(onSubmit)" class="d-flex h-100">
      <SidebarLayout :title="$t('Branches')">
        <template v-slot:buttons>
          <add-new-button v-if="enableSyncWebsiteFromArtisio" class="mr-2" @click="onPostBranchesOnWebsiteClick">
            {{ $t('Post Branches On Website') }}
          </add-new-button>
        </template>
        <template v-slot:sidebar>
          <Spinner :show="branches.loading"/>
          <ul v-if="!branches.loading" class="nav flex-column">
            <li v-for="(branch) in branches.rows" :key="branch.uuid" class="nav-item" @click="changeBranch(branch)"
                :class="{ 'active': branch.uuid === selectedBranch.uuid }">
              <a class="nav-link">
                {{ branch.name }}
              </a>
            </li>
          </ul>
        </template>
        <template v-slot:content>
          <div class="card" v-if="branches.loading">
            <div class="card-body">
              <Spinner show/>
            </div>
          </div>
          <artisio-tabs v-model="currentTab" v-if="!branches.loading" content-class="bg-white p-2">
            <home :model="model"/>
            <settings :model="model"/>
            <terms :model="model"/>
            <premiums :model="model"/>
            <charges/>
            <item-locations ref="itemLocationsTab"/>
            <clerks :model="model"/>
            <trust-levels :model="model"/>
            <auction-fields :model="model" :show="showAuctionFields"/>
            <vendor-charges />
            <contract-settings :model="model" v-if="isDocusignEnabled"/>
          </artisio-tabs>
        </template>
        <template v-slot:footer-buttons>
          <delete-button class="mr-2" :disabled="loading" @click="onBranchRemove"/>
          <submit-button :disabled="loading" :show-loader="loading"/>
        </template>
      </SidebarLayout>
    </b-form>
    <branch-modal/>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import BranchModal from "./BranchModal";
  import Spinner from "../../../core/components/common/Spinner";
  import SubmitButton from "../../../core/components/common/SubmitButton";
  import AddNewButton from "../../../core/components/common/AddNewButton";
  import BranchModel from "./Branch.model";
  import DeleteButton from "../../../core/components/common/DeleteButton";
  import Home from "./tabs/Home";
  import Settings from "./tabs/Settings";
  import Premiums from "./tabs/premiums/Premiums";
  import Clerks from "./tabs/clerks/Clerks";
  import Terms from "./tabs/terms/Terms";
  import ItemLocations from "./tabs/item-locations/ItemLocations";
  import SidebarLayout from "../../../core/components/layout/DefaultLayout/SidebarLayout";
  import {Utils} from "@/core/services/utils.service";
  import BranchService from "./BranchService";
  import EventBus from "@/plugins/event-bus";
  import {ON_CLEAR_FILE_UPLOADER_INPUT} from "@/plugins/event-bus/event-bus-names";
  import Charges from "@/modules/setup/branch/tabs/charges/Charges";
  import ArtisioTabs from "@/core/components/artisio-tabs/ArtisioTabs";
  import TrustLevels from "./tabs/trust-levels/TrustLevels";
  import AuctionFields from "@/modules/setup/branch/tabs/auction-fields/AuctionFields.vue";
  import VendorCharges from "@/modules/setup/branch/tabs/vendor-charges/VendorCharges";
  import ContractSettings from "@/modules/setup/branch/tabs/contract-settings/ContractSettings.vue";

  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapState, mapActions} = createNamespacedHelpers('setup/branch');

  export default {
    name: "Branch",
    components: {
      ContractSettings,
      TrustLevels,
      ArtisioTabs,
      SidebarLayout, ItemLocations, Terms, Home, Premiums, Charges,
      Settings, DeleteButton, AddNewButton, SubmitButton, Spinner, BranchModal, Clerks, AuctionFields, VendorCharges
    },
    data() {
      return {
        loading: false,
        oldState: null,
        model: new BranchModel(),
        currentTab: 0,
        uploadingProgressValue: 0,
        uploadingProgress: false,
        showAuctionFields: false
      }
    },
    computed: {
      ...mapState(['branches', 'selectedBranch', 'selectedBranchLogo']),
      ...mapStateGlobal({
        enableSyncWebsiteFromArtisio: state => state.enableSyncWebsiteFromArtisio,
        currentUser: state => state.currentUser,
        isDocusignEnabled: state => state.isDocusignEnabled,
      }),
    },
    watch: {
      selectedBranch() {
        if (this.selectedBranch && this.selectedBranch.uuid) {
          this.$refs.observer.reset();
          this.model = new BranchModel(this.selectedBranch);
          this.oldState = this.getState();
          this.$nextTick(() => {
            this.showAuctionFields = true
          })
        }
      }
    },
    methods: {
      ...mapActions(['showModal', 'getBranches', 'selectBranch', 'removeBranch', 'updateBranch', 'updateSelectedBranchLogo', 'createBranchLogo', 'deleteBranchLogo']),
      async onSubmit() {
        this.loading = true;

        if (this.model.contract_default_recipients && this.model.contract_default_recipients.length > 0) {
          let emailMap = {}
          for (const { email } of this.model.contract_default_recipients) {
            emailMap[email] = (emailMap[email] || 0) + 1
          }
          const duplicateEmails = Object.keys(emailMap).filter(
            email => emailMap[email] > 1
          )

          if (duplicateEmails.length > 0) {
            this.$errorDialog(this.$i18n.t('You cannot have duplicate email recipients under contract settings. Please, remove duplicates and try again. Duplicate email(s): {emails}', {emails: duplicateEmails.join(', ')}))
            this.loading = false;
            return
          }
        }

        // create attachment to set to branch logo
        if (this.selectedBranchLogo && this.selectedBranchLogo.file) {
          let formData = new FormData();
          formData.append('file', this.selectedBranchLogo.file);
          formData.append('branch_uuid', this.selectedBranch.uuid);
          this.uploadingProgress = true;
          let response = await this.createBranchLogo({
            formData, onUploadProgress: (progressEvent) => {
              this.uploadingProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
            }
          });
          this.uploadingProgress = false;
          this.uploadingProgressValue = 0;
          if (response.success) {
            let oldLogoUuid = this.model.logo_uuid;
            //set new attachment uuid to branch and update it
            this.model.logo_uuid = response.body[0].uuid;
            await this.updateCurrentBranch();
            //delete old attachment
            if (oldLogoUuid) {
              await this.deleteBranchLogo(oldLogoUuid)
            }
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0])
            }
          }
          this.loading = false;
        } else {
          this.loading = true;
          let oldLogoUuid = this.model.logo_uuid;
          if (oldLogoUuid && !this.selectedBranchLogo) {
            this.model.logo_uuid = null;
            await this.updateCurrentBranch(); // deletes logo from branch
            await this.deleteBranchLogo(oldLogoUuid) // deletes attachment itself
          } else {
            await this.updateCurrentBranch();
          }
          this.loading = false;
        }
      },
      async onBranchRemove() {
        this.$confirm(
            this.$i18n.t('Are you sure you want to delete branch {name}?', {name: this.selectedBranch.name}),
            this.$t('Branch {name} was successfully deleted', {name: this.selectedBranch.name}),
            this.removeBranch,
            this.selectedBranch.uuid
        );
      },
      async changeBranch(branch) {
        if (this.oldState && this.dataChanged()) {
          this.$confirm(this.$i18n.t('Are you sure you want discard current changes?')).then(async (success) => {
            if (success) this.selectBranch(branch)
          });
        } else {
          this.selectBranch(branch);
        }
        await this.updateSelectedBranchLogo(branch.logo);
        EventBus.$emit(ON_CLEAR_FILE_UPLOADER_INPUT);
      },
      getState() {
        if (!this.model) {
          return false;
        }
        const myData = {...this.model};
        delete myData.rules;
        delete myData.errors;
        delete myData.attributeLabels;
        myData.commissions.forEach(c => {
          delete c.customMessages
        });
        myData.premiums.forEach(p => {
          delete p.customMessages
        });
        return JSON.stringify(myData);
      },
      dataChanged() {
        return Utils.isDifferent(JSON.parse(this.getState()), JSON.parse(this.oldState));
      },
      async onPostBranchesOnWebsiteClick() {
        let response = await BranchService.postBranchesOnWebsite();
        if (response.success) {
          this.$toast('success', this.$i18n.t('Branches were posted successfully'));
        }
      },
      async updateCurrentBranch() {
        const res = await this.updateBranch(this.model);
        if (res.success) {
          this.$toast('success', this.$t('Branch {name} was successfully updated', {name: this.model.name}))
        } else {
          if (res.errors.length) {
            this.model.setMultipleErrors(res.body);
          } else if (res.body.non_field_errors) {
            this.$errorDialog(res.body.non_field_errors[0])
          } else {
            this.model.setMultipleErrors(res.body);
          }
        }
      }
    },
    async beforeMount() {
      await this.getBranches();

      if (this.$route.params.item_location_uuid) {
        this.changeBranch(this.branches.rows.find(b => b.uuid === this.$route.params.branch_uuid));
        this.currentTab = 5; // item locations tab index
        this.$nextTick(() => {
          this.$refs.itemLocationsTab.onEditClick(
              this.selectedBranch.item_locations.find(il => il.uuid === this.$route.params.item_location_uuid)
          );
        })
      } else if (this.$route.params.branch_uuid) {
        this.changeBranch(this.branches.rows.find(branch => branch.uuid === this.$route.params.branch_uuid))
      } else if (this.branches.rows.length > 0) {
        const index = this.branches.rows.findIndex(b => b.uuid === this.currentUser.branch.uuid)
        this.changeBranch(this.branches.rows[index]);
      }
    },
    beforeRouteLeave(to, from, next) {
      if (this.dataChanged()) {
        this.$confirm(this.$i18n.t('Are you sure you want discard current changes?')).then(async (success) => {
          if (success) next();
        });
      } else {
        next();
      }
    }
  }
</script>

<style lang="scss" scoped>

  .validation-observer {
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .artisio-tabs {
    /deep/ {
      .tabs {
        height: calc(100vh - 17em);
      }

      .tab-content {
        height: 90% !important;
        overflow-y: auto;
      }
    }
  }

</style>
